import { Schema } from "effect";

/** Signed playback URLs are valid for five minutes (CM-3061). */
export const CALL_AUDIO_PLAYBACK_TTL_SECONDS = 300;

/**
 * Signed bulk-download URLs stay valid for one hour. Playback keeps the
 * five-minute lifetime above. A batch is minted once, then the browser reads
 * each object directly.
 */
export const CALL_AUDIO_DOWNLOAD_TTL_SECONDS = 60 * 60;

/**
 * One Call Audio ZIP.
 *
 * There is no recording-count cap. The archive is limited to 200 MB of audio,
 * measured from size_bytes on archived Dialpad recordings and Daily track
 * sizes. The server reads one object at a time. A stalled object fails on its
 * own timeout; the playback URL lifetime does not bound the ZIP. Each filtered
 * statement stays under the 30 second replica statement timeout. Raising the
 * byte cap is a separate discussion.
 */
/** 200 MiB of archived audio. */
export const CALL_AUDIO_DOWNLOAD_BYTE_CAP = 209_715_200;

/** Manifest columns. The download modal lists these same fields. */
export const CALL_AUDIO_MANIFEST_FIELDS = [
  "When",
  "Source",
  "Recording id",
  "Call id",
  "Direction",
  "Duration seconds",
  "Handler name",
  "Handler email",
  "Handler user id",
  "Handler role",
  "Craniometrix number",
  "External number",
  "Carespace id",
  "Carespace name",
  "Clinic id",
  "Clinic name",
  "Caregiver name",
  "Caregiver email",
  "Caregiver phones",
  "Caregiver relationship",
  "Audio path",
  "Transcript path",
] as const;

export const CallAudioSource = Schema.Literals(["dialpad", "daily"]);
export type CallAudioSource = typeof CallAudioSource.Type;

export const CallAudioPlaybackTrack = Schema.Struct({
  contentType: Schema.String,
  url: Schema.String,
});
export type CallAudioPlaybackTrack = typeof CallAudioPlaybackTrack.Type;

export const CallAudioPlayback = Schema.Struct({
  expiresInSeconds: Schema.Literal(CALL_AUDIO_PLAYBACK_TTL_SECONDS),
  recordingId: Schema.String,
  source: CallAudioSource,
  tracks: Schema.NonEmptyArray(CallAudioPlaybackTrack),
});
export type CallAudioPlayback = typeof CallAudioPlayback.Type;

export const CALL_AUDIO_RECORDINGS_PAGE_SIZES = [25, 50, 100] as const;
export const CALL_AUDIO_RECORDINGS_PAGE_SIZE = 50;
export const CallAudioPageSize = Schema.Literals(CALL_AUDIO_RECORDINGS_PAGE_SIZES);
export type CallAudioPageSize = typeof CallAudioPageSize.Type;

export const CallAudioSortColumn = Schema.Literals([
  "occurredAt",
  "durationSeconds",
  "source",
  "navigatorName",
  "callType",
  "direction",
]);
export type CallAudioSortColumn = typeof CallAudioSortColumn.Type;

export const CallAudioSuggestionField = Schema.Literals([
  "navigator",
  "carespaceId",
  "callType",
  "clinic",
  "caregiver",
]);
export type CallAudioSuggestionField = typeof CallAudioSuggestionField.Type;

export const CallAudioHandlerRole = Schema.Literals([
  "Navigator",
  "Enrollment",
  "Admin",
  "Super admin",
  "No role",
]);
export type CallAudioHandlerRole = typeof CallAudioHandlerRole.Type;

/** Query values for the handler-role filter. `member` means no privileged role. */
export const CALL_AUDIO_HANDLER_ROLE_KEYS = [
  "care_navigator",
  "enrollment_specialist",
  "admin",
  "super_admin",
  "member",
] as const;
export const CallAudioHandlerRoleKey = Schema.Literals(CALL_AUDIO_HANDLER_ROLE_KEYS);
export type CallAudioHandlerRoleKey = typeof CallAudioHandlerRoleKey.Type;

export const CallAudioCarespaceMatch = Schema.Literals(["matched", "multiple", "none", "conflict"]);
export type CallAudioCarespaceMatch = typeof CallAudioCarespaceMatch.Type;

export const CallAudioCaregiver = Schema.Struct({
  email: Schema.NullOr(Schema.String),
  name: Schema.String,
  phones: Schema.Array(Schema.String),
  relationship: Schema.NullOr(Schema.String),
  status: Schema.String,
  type: Schema.String,
});
export type CallAudioCaregiver = typeof CallAudioCaregiver.Type;

export const CallAudioDialpadStatus = Schema.Literals(["available", "unavailable"]);
export type CallAudioDialpadStatus = typeof CallAudioDialpadStatus.Type;

export const CallAudioRecording = Schema.Struct({
  archiveStatus: Schema.String,
  assignedNavigatorEmail: Schema.NullOr(Schema.String),
  assignedNavigatorName: Schema.NullOr(Schema.String),
  callId: Schema.String,
  callType: Schema.String,
  caregiverCount: Schema.Number,
  caregivers: Schema.Array(CallAudioCaregiver),
  carespaceId: Schema.NullOr(Schema.String),
  carespaceMatch: CallAudioCarespaceMatch,
  carespaceName: Schema.NullOr(Schema.String),
  clinicId: Schema.NullOr(Schema.String),
  clinicName: Schema.NullOr(Schema.String),
  contactPhone: Schema.NullOr(Schema.String),
  direction: Schema.NullOr(Schema.Literals(["inbound", "outbound"])),
  durationSeconds: Schema.NullOr(Schema.Number),
  externalNumber: Schema.NullOr(Schema.String),
  handlerDialpadTargetId: Schema.NullOr(Schema.String),
  handlerEnrollmentList: Schema.Boolean,
  handlerRole: CallAudioHandlerRole,
  handlerUserId: Schema.NullOr(Schema.String),
  hasTranscript: Schema.Boolean,
  readableTranscript: Schema.Boolean,
  internalNumber: Schema.NullOr(Schema.String),
  navigatorEmail: Schema.NullOr(Schema.String),
  navigatorName: Schema.NullOr(Schema.String),
  occurredAt: Schema.String,
  recordingId: Schema.String,
  selectedCallerId: Schema.NullOr(Schema.String),
  source: CallAudioSource,
});
export type CallAudioRecording = typeof CallAudioRecording.Type;

/**
 * Archived recording list. Playback URLs are not part of this payload; the
 * play action calls the call-audio adapter, which mints a short-lived URL.
 */
export const CallAudioRecordingsResponse = Schema.Struct({
  dialpadStatus: CallAudioDialpadStatus,
  end: Schema.NullOr(Schema.String),
  generatedAt: Schema.String,
  nextCursor: Schema.NullOr(Schema.String),
  pageSize: CallAudioPageSize,
  prevCursor: Schema.NullOr(Schema.String),
  rows: Schema.Array(CallAudioRecording),
  start: Schema.NullOr(Schema.String),
  timezone: Schema.Literal("America/New_York"),
});
export type CallAudioRecordingsResponse = typeof CallAudioRecordingsResponse.Type;

/** Matching recordings for the current filters. Loaded beside the page. */
export const CallAudioRecordingsCount = Schema.Struct({
  total: Schema.Number,
});
export type CallAudioRecordingsCount = typeof CallAudioRecordingsCount.Type;

/** Autocomplete values for the lookup. Transcript text is never included. */
export const CallAudioSuggestionsResponse = Schema.Struct({
  field: CallAudioSuggestionField,
  values: Schema.Array(Schema.String),
});
export type CallAudioSuggestionsResponse = typeof CallAudioSuggestionsResponse.Type;

/**
 * Spoken transcript for one recording. Loaded on demand and never cached with
 * the recording list.
 */
export const CallAudioTranscript = Schema.Struct({
  callType: Schema.String,
  carespaceId: Schema.NullOr(Schema.String),
  durationSeconds: Schema.NullOr(Schema.Number),
  navigatorEmail: Schema.NullOr(Schema.String),
  navigatorName: Schema.NullOr(Schema.String),
  occurredAt: Schema.String,
  recordingId: Schema.String,
  source: CallAudioSource,
  text: Schema.String,
});
export type CallAudioTranscript = typeof CallAudioTranscript.Type;

/** Byte budget for the filtered ZIP. Transcript text is not included. */
export const CallAudioDownloadPreview = Schema.Struct({
  byteCap: Schema.Literal(CALL_AUDIO_DOWNLOAD_BYTE_CAP),
  fields: Schema.Array(Schema.String),
  matchedBytes: Schema.NullOr(Schema.Number),
  matchedRows: Schema.Number,
  overByteCap: Schema.Boolean,
  unknownAudioBytes: Schema.Boolean,
});
export type CallAudioDownloadPreview = typeof CallAudioDownloadPreview.Type;

/**
 * One filtered download. Text files are inline. Audio files are presigned URLs
 * and are absent when audio is unchecked. The control plane does not read the
 * audio to build this.
 */
export const CallAudioDownloadManifestFile = Schema.Struct({
  name: Schema.String,
  text: Schema.optional(Schema.String),
  url: Schema.optional(Schema.String),
});
export type CallAudioDownloadManifestFile = typeof CallAudioDownloadManifestFile.Type;

export const CallAudioDownloadManifestFailure = Schema.Struct({
  message: Schema.String,
  name: Schema.String,
});
export type CallAudioDownloadManifestFailure = typeof CallAudioDownloadManifestFailure.Type;

export const CallAudioDownloadManifest = Schema.Struct({
  expiresInSeconds: Schema.NullOr(Schema.Literal(CALL_AUDIO_DOWNLOAD_TTL_SECONDS)),
  failures: Schema.Array(CallAudioDownloadManifestFailure),
  files: Schema.Array(CallAudioDownloadManifestFile),
});
export type CallAudioDownloadManifest = typeof CallAudioDownloadManifest.Type;
